import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import {
  ApiError,
  inviteUser,
  issuePasswordReset,
  listCompanyUsers,
  listInvitations,
  revokeInvitation,
  updateCompanyUser,
  type CompanyUser,
  type InvitationIssued,
  type Me,
  type PendingInvitation,
  type ResetIssued,
} from '../api.js';
import { ROLES, formatDate, roleLabel, type Role } from '../labels.js';
import { RegisterToolbar, matches, useRegisterView } from './register-kit.js';

/**
 * The people in a company: who is in it, who has been asked, and their roles.
 *
 * NOTHING SENDS EMAIL YET, AND THE SCREEN SAYS SO
 * -----------------------------------------------
 * There is no mail server configured. An invitation therefore shows its link
 * once, for the admin to send however they already talk to people. Only the
 * hash of the token is stored, so the link genuinely cannot be shown again —
 * the screen has to be honest about that, because someone who closes the panel
 * expecting to find it later would be stuck.
 */

interface Props {
  readonly me: Me;
}

export function People({ me }: Props): JSX.Element {
  const [users, setUsers] = useState<readonly CompanyUser[] | null>(null);
  const [invitations, setInvitations] = useState<readonly PendingInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<InvitationIssued | null>(null);
  /** The reset link, held only until the admin dismisses it. Never refetched. */
  const [reset, setReset] = useState<ResetIssued | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * The toolbar is on the PEOPLE table only.
   *
   * The invitations below it are a short list of things awaiting an answer —
   * a company with forty staff has two or three — and a search box over them
   * would be furniture.
   */
  const view = useRegisterView('name');
  /**
   * The tab is hidden without `view_users`, but the address bar is not. Anybody
   * who types `/people` lands here, and a red banner would read as a fault in
   * the system rather than as a role that simply does not cover this.
   */
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [people, invites] = await Promise.all([listCompanyUsers(), listInvitations()]);
      setUsers(people);
      setInvitations(invites);
      setError(null);
      setDenied(false);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
        setDenied(true);
        setUsers([]);
        setError(null);
        return;
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not load your people.');
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The people on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps`. It runs before the early
   * returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!users) return [];

    const role = view.chosen.role;
    const status = view.chosen.status;

    const filtered = users.filter((user) => {
      // Name, email and the role in the words the screen shows — somebody
      // looking for "the QS" should not have to know the internal key.
      const hay = [user.name, user.email, roleLabel(user.company_role)].join(' ');
      if (!matches(hay, view.query)) return false;
      if (role && user.company_role !== role) return false;
      if (status && user.status !== status) return false;
      return true;
    });

    const compare: Readonly<Record<string, (a: CompanyUser, b: CompanyUser) => number>> = {
      name: (a, b) => a.name.localeCompare(b.name),
      role: (a, b) =>
        roleLabel(a.company_role).localeCompare(roleLabel(b.company_role)) ||
        a.name.localeCompare(b.name),
      // Busiest first: the person on eleven jobs is the one somebody is
      // usually looking for when they open this screen.
      projects: (a, b) => b.project_count - a.project_count,
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.name!);
  }, [users, view.query, view.chosen.role, view.chosen.status, view.sortKey]);

  async function run(work: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (denied) {
    return (
      <section>
        <div className="section-head">
          <h2>People</h2>
        </div>
        <p className="hint">Your role does not include the people in this company.</p>
      </section>
    );
  }

  return (
    <section>
      <div className="section-head">
        <h2>People</h2>
        {me.can.invite_users && !inviting && (
          <button type="button" className="inline" onClick={() => setInviting(true)}>
            Invite someone
          </button>
        )}
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {issued && <InvitationLink issued={issued} onDismiss={() => setIssued(null)} />}

      {reset && <ResetLink issued={reset} onDismiss={() => setReset(null)} />}

      {inviting && (
        <InviteForm
          busy={busy}
          onCancel={() => {
            setInviting(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            const ok = await run(async () => {
              setIssued(await inviteUser(input));
              setInvitations(await listInvitations());
            });
            if (ok) setInviting(false);
          }}
        />
      )}

      <h3 className="sub-head">In your company{users ? ` (${users.length})` : ''}</h3>

      {users === null && <p aria-live="polite">Loading…</p>}

      {users && users.length > 0 && (
        <RegisterToolbar
          view={view}
          searchLabel="Search people"
          searchPlaceholder="Name, email, role"
          shown={shown.length}
          total={users.length}
          noun={['person', 'people']}
          filters={[
            {
              key: 'role',
              label: 'Role',
              options: ROLES.map((key) => ({ value: key, label: roleLabel(key) })),
            },
            {
              key: 'status',
              label: 'Status',
              options: [
                { value: 'active', label: 'Active' },
                // `suspended` is the server's word for it — see
                // `users/service.ts`. The screen says "Switched off", which is
                // what an admin calls it, and the FILTER has to send the other.
                { value: 'suspended', label: 'Switched off' },
              ],
            },
          ]}
          sorts={[
            { key: 'name', label: 'Name' },
            { key: 'role', label: 'Role' },
            { key: 'projects', label: 'Most projects' },
          ]}
        />
      )}

      {users && users.length > 0 && shown.length === 0 && (
        /*
         * A DIFFERENT empty state from a company with nobody in it: this
         * reader has colleagues and a filter that excludes all of them.
         */
        <div className="empty">
          <p>
            <strong>Nobody matches that.</strong>
          </p>
          <p>
            <button type="button" className="link-button" onClick={view.clear}>
              Clear the search and filters
            </button>{' '}
            to see all {users.length}.
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <table className="grid rk-cards">
          <caption className="visually-hidden">
            People in your company. {shown.length} of {users?.length ?? 0} shown.
          </caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Projects</th>
              <th scope="col">Status</th>
              {me.can.edit_users && (
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {shown.map((user) =>
              editing === user.user_id ? (
                <EditUserRow
                  key={user.user_id}
                  user={user}
                  isSelf={user.user_id === me.user_id}
                  busy={busy}
                  onCancel={() => setEditing(null)}
                  onSubmit={async (input) => {
                    const ok = await run(async () => {
                      setUsers(await updateCompanyUser(user.user_id, input));
                    });
                    if (ok) setEditing(null);
                  }}
                />
              ) : (
                <tr key={user.user_id}>
                  <td data-label="Name">
                    {user.name}
                    {user.user_id === me.user_id && <span className="tree-code"> (you)</span>}
                  </td>
                  <td className="mono" data-label="Email">{user.email}</td>
                  <td data-label="Role">{roleLabel(user.company_role)}</td>
                  <td data-label="Projects">{user.project_count}</td>
                  <td data-label="Status">{user.status === 'active' ? 'Active' : 'Switched off'}</td>
                  {me.can.edit_users && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="link-button"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setEditing(user.user_id);
                        }}
                      >
                        Change<span className="visually-hidden"> {user.name}</span>
                      </button>
                      {/*
                        * Offered for everybody INCLUDING yourself. An admin who
                        * wants to change their own password has no other route
                        * to one, and refusing here would only send them to ask
                        * a colleague to do it for them.
                        */}
                      <button
                        type="button"
                        className="link-button"
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            setIssued(null);
                            setReset(await issuePasswordReset(user.user_id));
                          })
                        }
                      >
                        Reset password
                        <span className="visually-hidden"> for {user.name}</span>
                      </button>
                    </td>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}

      <h3 className="sub-head">Invited, not yet joined{invitations.length ? ` (${invitations.length})` : ''}</h3>

      {invitations.length === 0 ? (
        <p className="hint">Nobody is waiting on an invitation.</p>
      ) : (
        <table className="grid">
          <caption className="visually-hidden">Outstanding invitations</caption>
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Invited by</th>
              <th scope="col">Link valid until</th>
              {me.can.revoke_invitations && (
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {invitations.map((invite) => (
              <tr key={invite.id}>
                <td className="mono" data-label="Email">{invite.email}</td>
                <td data-label="Role">{roleLabel(invite.company_role)}</td>
                <td data-label="Invited by">{invite.invited_by ?? '—'}</td>
                <td data-label="Link valid until">
                  {invite.expired ? (
                    // Shown, not hidden: an admin wondering why someone never
                    // arrived needs to see that the link ran out.
                    <strong>Expired</strong>
                  ) : (
                    formatDate(invite.expires_at)
                  )}
                </td>
                {me.can.revoke_invitations && (
                  <td className="row-actions">
                    <button
                      type="button"
                      className="link-button danger"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => setInvitations(await revokeInvitation(invite.id)))
                      }
                    >
                      Cancel<span className="visually-hidden"> the invitation to {invite.email}</span>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * The reset link, shown once.
 *
 * Nearly the same panel as `InvitationLink`, and deliberately NOT merged with
 * it. The two say different things — one is "here is how somebody joins", the
 * other is "here is how somebody gets back in, and it will sign them out of
 * their phone" — and a single component with a mode flag would end up printing
 * the wrong sentence the first time either changes.
 */
function ResetLink({
  issued,
  onDismiss,
}: {
  readonly issued: ResetIssued;
  readonly onDismiss: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${issued.path}`;

  return (
    <div className="panel">
      <p className="panel-title">
        Send this link to {issued.name} ({issued.email})
      </p>
      <p className="hint">
        There is no email server set up yet, so send it however you normally would — and check
        you are sending it to the right person, because whoever opens it sets the password.{' '}
        <strong>This is the only time it can be shown</strong> — the system stores only a
        scrambled copy. Issuing another one cancels this. The link stops working on{' '}
        {formatDate(issued.expires_at)}, and using it signs {issued.name} out on every device.
      </p>
      <p className="invite-link mono">{url}</p>
      <div className="button-row">
        <button
          type="button"
          className="inline"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <button type="button" className="secondary inline" onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function InvitationLink({
  issued,
  onDismiss,
}: {
  readonly issued: InvitationIssued;
  readonly onDismiss: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${issued.path}`;

  return (
    <div className="panel">
      <p className="panel-title">Send this link to {issued.email}</p>
      <p className="hint">
        There is no email server set up yet, so send it however you normally would.{' '}
        <strong>This is the only time it can be shown</strong> — the system stores only a
        scrambled copy, so it cannot be looked up again. If it is lost, cancel the invitation
        and send a new one. The link stops working on {formatDate(issued.expires_at)}.
      </p>
      <p className="invite-link mono">{url}</p>
      <div className="button-row">
        <button
          type="button"
          className="inline"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <button type="button" className="secondary inline" onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface InviteProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { email: string; company_role: string }) => void | Promise<void>;
}

function InviteForm({ busy, onCancel, onSubmit }: InviteProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('site_engineer');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!email.trim()) return;
    void onSubmit({ email: email.trim(), company_role: role });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="invite-email">Email address</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            disabled={busy}
            placeholder="name@company.ae"
          />
        </div>
        <div className="field">
          <label htmlFor="invite-role">Role in the company</label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={busy}
          >
            {ROLES.map((key) => (
              <option key={key} value={key}>
                {roleLabel(key)}
              </option>
            ))}
          </select>
          <p className="hint">
            They can hold a different role on each project — this is their default.
          </p>
        </div>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !email.trim()}>
          {busy ? 'Creating…' : 'Create invitation'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  readonly user: CompanyUser;
  readonly isSelf: boolean;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    company_role: string;
    status: 'active' | 'suspended';
  }) => void | Promise<void>;
}

function EditUserRow({ user, isSelf, busy, onCancel, onSubmit }: EditProps): JSX.Element {
  const [role, setRole] = useState<Role>(user.company_role as Role);
  const [active, setActive] = useState(user.status === 'active');

  return (
    <tr className="editing">
      <td data-label="Name">{user.name}</td>
      <td className="mono" data-label="Email">{user.email}</td>
      <td data-label="Role">
        <label className="visually-hidden" htmlFor={`role-${user.user_id}`}>
          Company role for {user.name}
        </label>
        <select
          id={`role-${user.user_id}`}
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          disabled={busy}
        >
          {ROLES.map((key) => (
            <option key={key} value={key}>
              {roleLabel(key)}
            </option>
          ))}
        </select>
      </td>
      <td data-label="Projects">{user.project_count}</td>
      <td data-label="Status">
        <div className="check compact">
          <input
            id={`active-${user.user_id}`}
            type="checkbox"
            checked={active}
            // Switching yourself off would sign you out mid-click. The server
            // would allow it; the screen should not invite it.
            disabled={busy || isSelf}
            onChange={(e) => setActive(e.target.checked)}
          />
          <label htmlFor={`active-${user.user_id}`}>
            Active
            {isSelf && <span className="hint">{' · '}You cannot switch off your own account here.</span>}
          </label>
        </div>
      </td>
      <td className="row-actions">
        <button
          type="button"
          className="link-button"
          disabled={busy}
          onClick={() =>
            void onSubmit({ company_role: role, status: active ? 'active' : 'suspended' })
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </td>
    </tr>
  );
}
